import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useCursorContext } from "../context/CursorContext";
import useDimension from "../hooks/useDimension";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";

const Navigation: React.FC = () => {
  // Get the current location from the router
  const location = useLocation();

  // Custom hook to get the dimensions of the window
  const dimension = useDimension();

  // Custom cursor context for mouse events
  const { mouseOverEvent, mouseOutEvent } = useCursorContext();

  // Language context
  const { language, setLanguage, t } = useLanguage();

  // Theme context
  const { theme, toggleTheme } = useTheme();

  // Derived theme colors
  const isDark = theme === "dark";
  const accentBg = isDark ? "bg-[#ece7e1]" : "bg-[#1a1818]";
  const accentText = isDark ? "text-[#1a1818]" : "text-[#ece7e1]";

  // Get current year and split into digits
  const currentYear = Array.from(String(new Date().getFullYear()));

  // Text follows the theme.
  const textColorClass = isDark
    ? "text-[#ece7e1]"
    : "";

  // Render navigation links conditionally based on the current route
  return (
    <div className={`${textColorClass} z-40 relative`}>
      {location.pathname !== "/" ? (
        <div className="z-50 w-full md:w-24 h-20 md:h-screen flex flex-row md:flex-col justify-start items-center pb-10 pt-10 md:pt-16 absolute md:fixed text-[11px] sm500:text-xs font-medium">
          {/* Home or Work link based on the current route */}
          <div className="md:w-16 md:mb-8 ml-6 sm500:ml-8 md:ml-0 mr-4 sm500:mr-8 md:mr-16 md:pt-16 md:-rotate-90">
            {location.pathname === "/art-connection" ||
              location.pathname === "/wonder" ||
              location.pathname === "/crypto-base" ? (
              <Link
                to="/work"
                onMouseOver={mouseOverEvent}
                onMouseOut={mouseOutEvent}
                className="uppercase py-2 hover:opacity-50 duration-300 ease-in-out"
              >
                {t("nav.work")}
              </Link>
            ) : (
              <Link
                to="/"
                onMouseOver={mouseOverEvent}
                onMouseOut={mouseOutEvent}
                className="uppercase py-2 hover:opacity-50 duration-300 ease-in-out"
              >
                {t("nav.home")}
              </Link>
            )}
          </div>

          {/* Vertical divider */}
          <div
            className={`${isDark
              ? "bg-[#ece7e1]"
              : "bg-[#1a1818]"
              } w-0 sm350:w-12 sm500:w-24 md:w-[1px] h-[1px] md:h-24 -mt-1 md:mt-2 md:mb-20 opacity-75`}
          ></div>

          {/* Year display */}
          <div className="md:mb-16 md:pl-10 md:pr-16 md:-rotate-90 w-auto md:w-28 ml-auto pr-6 sm500:pr-8 md:pr-0 md:absolute md:right-auto md:top-auto md:bottom-5">
            <div className="text-center flex justify-end md:justify-center items-center gap-1 text-[10px] md:text-[11px]">
              <span>©</span>
              <span>/</span>
              {currentYear.map((digit, index) => (
                <span key={index}>{digit}</span>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="z-50 w-full md:w-24 h-20 md:h-screen flex flex-row md:flex-col justify-start items-center pb-10 pt-10 md:pt-16 fixed text-[11px] sm500:text-xs font-medium">
          {/* External links */}
          <div className="ml-4 sm500:ml-6 md:ml-0 mr-4 md:mr-16 md:pt-16 md:-rotate-90">
            <Link
              to="/contact"
              onMouseOver={mouseOverEvent}
              onMouseOut={mouseOutEvent}
              className="py-2 hover:opacity-50 duration-300 ease-in-out"
            >
              {dimension.width < 500 ? "G M" : "Gmail"}
            </Link>
          </div>
          <div className="mr-4 md:mr-16 md:pt-16 md:-rotate-90">
            <Link
              to="https://github.com/EstevanV-Q"
              target="_blank"
              onMouseOver={mouseOverEvent}
              onMouseOut={mouseOutEvent}
              className="py-2 hover:opacity-50 duration-300 ease-in-out flex items-center justify-center"
            >
              {/* Mobile Icon */}
              <svg className="block md:hidden w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.43.372.823 1.102.823 2.222 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
              </svg>
              {/* Desktop Text */}
              <span className="hidden md:block">
                {dimension.width < 500 ? "G H" : "GitHub"}
              </span>
            </Link>
          </div>
          <div className="md:mb-8 mr-4 sm500:mr-8 md:mr-16 md:pt-16 md:-rotate-90">
            <Link
              to="https://www.linkedin.com/in/estebanvdev"
              target="_blank"
              onMouseOver={mouseOverEvent}
              onMouseOut={mouseOutEvent}
              className="py-2 hover:opacity-50 duration-300 ease-in-out flex items-center justify-center"
            >
              {/* Mobile Icon */}
              <svg className="block md:hidden w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
              {/* Desktop Text */}
              <span className="hidden md:block">
                {dimension.width < 500 ? "L I" : "LinkedIn"}
              </span>
            </Link>
          </div>

          {/* Vertical divider */}
          <div className="w-0 sm500:w-12 sm:w-24 md:w-[1px] h-[1px] md:h-24 opacity-75 theme-bg-inv -mt-1 md:mt-2 md:mb-20"></div>

          {/* Language Toggle */}
          <div className="mr-4 sm500:mr-8 md:mr-16 md:pt-16 md:-rotate-90 flex gap-2 items-center text-[11px] font-medium uppercase !cursor-none">
            <button
              onClick={() => setLanguage("en")}
              onMouseOver={mouseOverEvent}
              onMouseOut={mouseOutEvent}
              className={`${language === "en" ? "opacity-100" : "opacity-40"
                } py-2 hover:opacity-50 duration-300 ease-in-out !cursor-none`}
            >
              EN
            </button>
            <span className="opacity-20 py-2 !cursor-none">/</span>
            <button
              onClick={() => setLanguage("es")}
              onMouseOver={mouseOverEvent}
              onMouseOut={mouseOutEvent}
              className={`${language === "es" ? "opacity-100" : "opacity-40"
                } py-2 hover:opacity-50 duration-300 ease-in-out !cursor-none`}
            >
              ES
            </button>
          </div>

          {/* Theme Toggle */}
          <div className="mr-4 sm500:mr-8 md:mr-16 md:pt-16 md:-rotate-90 flex gap-2 items-center text-[11px] font-medium uppercase !cursor-none">
            <button
              onClick={toggleTheme}
              onMouseOver={mouseOverEvent}
              onMouseOut={mouseOutEvent}
              className="py-2 flex items-center gap-2 hover:opacity-50 duration-300 ease-in-out !cursor-none"
            >
              <span className={`w-7 h-3.5 rounded-full relative transition-colors duration-400 ${isDark ? accentBg : "bg-[#1a1818]/20"}`}>
                <span
                  className={`absolute top-0.5 w-2.5 h-2.5 rounded-full transition-all duration-300 ${isDark ? `right-0.5 ${accentText} bg-[#ece7e1]` : "left-0.5 bg-[#1a1818]"}`}
                />
              </span>
              {isDark ? "Dark" : "Light"}
            </button>
          </div>

          {/* Year display */}
          <div className="md:mb-16 md:pl-10 md:pr-16 md:-rotate-90 w-auto md:w-28 ml-auto pr-4 sm500:pr-8 md:pr-0 md:absolute md:right-auto md:top-auto md:bottom-5">
            <div className="text-center flex justify-end md:justify-center items-center gap-1 text-[10px] md:text-[11px]">
              <span>©</span>
              <span>/</span>
              {currentYear.map((digit, index) => (
                <span key={index}>{digit}</span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Navigation;
